// AllocationDeltaEditor — guided qty editor for EventStockAllocation.
//
// Two modes:
//   - delta:    modify the current qty by a relative amount (+/-).
//               Used when editing an existing allocation. The current
//               qty is shown, user types the exact delta, sees a live
//               "New qtyIn: X" preview, and types a reason before saving.
//   - absolute: set the initial qty from 0. Used when adding a new
//               product allocation. Same input UI, but starting at 0.
//
// Design choice — NO quick-step chips and NO reason suggestion chips:
//   This editor is the single place where stock allocation can change,
//   and every change goes into the immutable audit log. Owners asked
//   us to make the UI deliberately slower — no "press a chip to add
//   5" shortcut, no "tap a reason phrase" shortcut — so every change
//   is a deliberate typing decision. The cost is a few extra seconds
//   per edit; the benefit is fewer misclicks and more meaningful
//   audit log entries. The live preview still makes the math obvious
//   so the user can sanity-check before saving.
//
// Validation:
//   - newQty must be >= minQty (typically 0 for absolute, qtySold for
//     delta — can't undo a sale).
//   - newQty <= maxQty if provided (sanity cap).
//   - If newQty < qtySold (e.g. user tried to lower below sold), show
//     an inline warning and disable Save.

import { useState } from 'react';
import { Save, X, AlertTriangle, ArrowRight, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FitText } from '@/components/ui/FitText';
import { cn, formatNumber } from '@/lib/format';

export type AllocationEditorMode = 'delta' | 'absolute';

export interface AllocationDeltaEditorProps {
  mode: AllocationEditorMode;
  /** Current qtyIn (delta mode only). For absolute mode, ignored. */
  currentQty?: number;
  /** Hard lower bound on newQty. For delta mode, usually qtySold. */
  minQty: number;
  /** Hard upper bound on newQty (optional, defaults to no cap). */
  maxQty?: number;
  /** Sold qty for warning display (delta mode only). */
  soldQty?: number;
  /** Reason for the change (required if requireReason is true). */
  requireReason?: boolean;
  /** Free-text pre-filled into the reason field (e.g. for new picks). */
  defaultReason?: string;
  /** Product display name — reserved for future use (e.g. warnings). */
  productName?: string;
  /** Disabled while the parent mutation is in flight. */
  saving?: boolean;
  /** Server-side error to surface inline. */
  error?: string | null;
  /**
   * Batch fields (lot, expiry, unit, order form). Shown when `showBatch` is
   * set; they take a full-width row of their own below qty and reason, because
   * squeezed into a half-column they were unreadable.
   */
  showBatch?: boolean;
  /**
   * Batch fields shown but not editable. Set once the batch has sold: its lot
   * and expiry are printed on invoices, so from then on only qty may move.
   */
  batchLocked?: boolean;
  batchLockedReason?: string;
  defaultLotNumber?: string;
  defaultExpiryDate?: string;
  defaultUnit?: string;
  defaultOrderForm?: string;
  /** Save handler. Receives the new qty, the delta, the reason and the batch. */
  onSave: (input: {
    newQty: number;
    delta: number;
    reason: string;
    lotNumber?: string;
    expiryDate?: string;
    unit?: string;
    orderForm?: string;
  }) => void;
  /** Cancel / close handler. */
  onCancel: () => void;
  /** Optional className for the outer wrapper. */
  className?: string;
}

export function AllocationDeltaEditor({
  mode,
  showBatch = false,
  batchLocked = false,
  batchLockedReason,
  defaultLotNumber = '',
  defaultExpiryDate = '',
  defaultUnit = '',
  defaultOrderForm = '',
  currentQty = 0,
  minQty,
  maxQty,
  soldQty = 0,
  requireReason = false,
  defaultReason = '',
  productName: _productName,
  saving = false,
  error = null,
  onSave,
  onCancel,
  className,
}: AllocationDeltaEditorProps) {
  const [deltaInput, setDeltaInput] = useState('');

  // Parse the user's typed delta. Empty / invalid → 0.
  const parsedDelta = parseInt(deltaInput, 10);
  const delta = Number.isNaN(parsedDelta) ? 0 : parsedDelta;

  const newQty = mode === 'delta' ? currentQty + delta : Math.max(0, delta);
  const absoluteNewQty = Math.max(0, newQty);

  // Validation
  const belowMin = absoluteNewQty < minQty;
  const aboveMax = maxQty !== undefined && absoluteNewQty > maxQty;
  const belowSold =
    mode === 'delta' && soldQty > 0 && absoluteNewQty < soldQty;
  const invalid = belowMin || aboveMax;

  const isNoOp = mode === 'delta' && delta === 0;
  const reasonMissing = requireReason && deltaInput === '' && false; // reason validated below
  void reasonMissing; // (kept for clarity; not used directly)

  const [reason, setReason] = useState(defaultReason);
  const [lotNumber, setLotNumber] = useState(defaultLotNumber);
  const [expiryDate, setExpiryDate] = useState(defaultExpiryDate);
  const [unit, setUnit] = useState(defaultUnit);
  const [orderForm, setOrderForm] = useState(defaultOrderForm);
  const reasonEmpty = requireReason && !reason.trim();
  const canSave = !invalid && !isNoOp && !reasonEmpty && !saving;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      newQty: absoluteNewQty,
      delta: mode === 'delta' ? delta : absoluteNewQty,
      reason: reason.trim(),
      ...(showBatch
        ? {
            lotNumber: lotNumber.trim(),
            expiryDate: expiryDate || undefined,
            unit: unit.trim() || undefined,
            orderForm: orderForm.trim(),
          }
        : {}),
    });
  };

  // ±1 quick nudges — just one-tap +/- on the field, NOT a full quick-
  // step grid. The owner wanted "no quick chips" but a single inline
  // -1 / +1 is so basic (it's the browser-default for number inputs
  // anyway) that it doesn't conflict with the "no shortcut" intent.
  // We make it small and visually de-emphasised so the dominant
  // action is still "type the delta yourself".
  const adjustBy = (n: number) => {
    const next = (Number.isNaN(parsedDelta) ? 0 : parsedDelta) + n;
    setDeltaInput(String(next));
  };

  return (
    <div
      className={cn(
        'p-4 rounded-lg border bg-canvas/50 space-y-3',
        invalid ? 'border-danger/40 bg-danger/5' : 'border-line',
        className,
      )}
    >
      {/* Header: current → preview (delta mode) or just new (absolute) */}
      <div className="flex items-center gap-3 flex-wrap">
        {mode === 'delta' && (
          <>
            <Stat label="Current" value={formatNumber(currentQty)} tone="default" />
            <ArrowRight size={14} className="text-ink-subtle shrink-0" />
          </>
        )}
        <Stat
          label={mode === 'delta' ? 'New qtyIn' : 'Initial qtyIn'}
          value={formatNumber(absoluteNewQty)}
          tone={
            invalid
              ? 'danger'
              : mode === 'delta' && delta !== 0
                ? 'primary'
                : 'default'
          }
        />
        {mode === 'delta' && delta !== 0 && (
          <span
            className={cn(
              'text-caption font-medium tabular-nums',
              delta > 0 ? 'text-success' : 'text-danger',
            )}
          >
            ({delta > 0 ? '+' : ''}
            {formatNumber(delta)})
          </span>
        )}
        {belowSold && (
          <span className="inline-flex items-center gap-1 text-caption text-warning">
            <AlertTriangle size={12} />
            Di bawah {formatNumber(soldQty)} yang sudah terjual
          </span>
        )}
        {aboveMax && (
          <span className="inline-flex items-center gap-1 text-caption text-danger">
            <AlertTriangle size={12} />
            Melebihi batas maksimum ({formatNumber(maxQty!)})
          </span>
        )}
      </div>

      {/* Delta input + reason. Both required to be hand-typed so
          every allocation change is a deliberate decision. */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-small font-medium text-ink mb-1.5">
            {mode === 'delta' ? 'Delta (bisa positif / negatif)' : 'Qty'}
          </label>
          <div className="flex">
            <button
              type="button"
              onClick={() => adjustBy(-1)}
              className="shrink-0 w-10 h-[42px] rounded-l border border-r-0 border-line bg-canvas text-ink-subtle hover:text-ink hover:bg-line/30 transition-colors inline-flex items-center justify-center"
              aria-label="Kurangi 1"
              title="−1"
            >
              <Minus size={14} />
            </button>
            <input
              type="number"
              inputMode="numeric"
              value={deltaInput}
              onChange={(e) => setDeltaInput(e.target.value)}
              placeholder={mode === 'delta' ? 'cth: -3 atau +25' : 'cth: 50'}
              min={mode === 'absolute' ? 1 : undefined}
              className="input rounded-none text-right tabular-nums"
              aria-label={mode === 'delta' ? 'Delta qty' : 'Initial qty'}
            />
            <button
              type="button"
              onClick={() => adjustBy(1)}
              className="shrink-0 w-10 h-[42px] rounded-r border border-l-0 border-line bg-canvas text-ink-subtle hover:text-ink hover:bg-line/30 transition-colors inline-flex items-center justify-center"
              aria-label="Tambah 1"
              title="+1"
            >
              <Plus size={14} />
            </button>
          </div>
          <p className="mt-1.5 text-caption text-ink-subtle">
            {mode === 'delta'
              ? `Ketik nilai delta. Negatif untuk kurangi (mis. -5), positif untuk tambah (mis. +10).`
              : 'Ketik jumlah qty yang akan dibawa ke event.'}
          </p>
        </div>
        <Input
          label={requireReason ? 'Alasan (wajib)' : 'Alasan (opsional)'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            mode === 'delta'
              ? 'Tulis alasan perubahan, mis. "Tambah stok dari gudang sentral"'
              : 'Tulis alasan, mis. "Initial stock untuk opening booth"'
          }
          hint={
            requireReason
              ? 'Dicatat di audit log. Wajib supaya ada jejak yang jelas.'
              : 'Dicatat di audit log. Sebaiknya diisi untuk perubahan yang signifikan.'
          }
        />
        {showBatch && (
          <div className="sm:col-span-2 space-y-2 pt-3 border-t border-line">
            {batchLocked && (
              <p className="text-caption text-ink-subtle flex items-start gap-1.5">
                <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warning" />
                <span>{batchLockedReason ?? 'Lot ini sudah terjual — lot, expired date, satuan dan form order terkunci. Hanya qty yang bisa diubah.'}</span>
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Input
                label="Lot number"
                placeholder="mis. LOT-2026-04"
                hint={batchLocked ? undefined : 'Kosongkan kalau tidak ada'}
                value={lotNumber}
                disabled={batchLocked}
                onChange={(e) => setLotNumber(e.target.value)}
              />
              <Input
                label="Expired date"
                type="date"
                hint={batchLocked ? undefined : 'Kosongkan kalau tidak kedaluwarsa'}
                value={expiryDate}
                disabled={batchLocked}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
              <Input
                label="Satuan"
                placeholder="pcs"
                hint={batchLocked ? undefined : 'pcs, box, botol…'}
                value={unit}
                disabled={batchLocked}
                onChange={(e) => setUnit(e.target.value)}
              />
              <Input
                label="Form order"
                placeholder="mis. FO-2026-0012"
                hint={batchLocked ? undefined : 'Nomor form order, kalau ada'}
                value={orderForm}
                disabled={batchLocked}
                onChange={(e) => setOrderForm(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Server error from the parent */}
      {error && (
        <div className="p-2.5 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={saving}
          leftIcon={<X size={14} />}
        >
          Batal
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!canSave}
          leftIcon={<Save size={14} />}
          title={
            invalid
              ? 'Nilai tidak valid'
              : isNoOp
                ? 'Tidak ada perubahan'
                : reasonEmpty
                  ? 'Alasan wajib diisi'
                  : `Simpan qtyIn baru: ${formatNumber(absoluteNewQty)}`
          }
        >
          Simpan {formatNumber(absoluteNewQty)}
        </Button>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'default' | 'primary' | 'danger';
}) {
  const toneClass = {
    default: 'text-ink',
    primary: 'text-primary',
    danger: 'text-danger',
  }[tone];
  return (
    <div>
      <p className="text-overline uppercase text-ink-subtle font-medium tracking-wider">
        {label}
      </p>
      <FitText
        className={cn('text-metric font-display font-semibold tabular-nums', toneClass)}
      >
        {value}
      </FitText>
    </div>
  );
}
